/**
 * Black & white brands: RiskLo's surfaces, borders and muted text use a slate /
 * navy palette written straight into the stylesheets (rgba(15, 23, 42, …),
 * #0f172a, #94a3b8 …), so the --bt-* theme variables can't reach them. When the
 * black & white style is on, this reads the app's own stylesheets once and adds
 * an override sheet where each of those slate colors becomes a neutral gray
 * of about the same brightness. Dark-mode rules only apply in dark mode and
 * light-mode rules only in light mode, so contrast stays what it was.
 * Stylesheets loaded later (lazy pages) are picked up too.
 */

const STYLE_ID = 'risklo-brand-mono-surfaces';
const SCOPE = 'html[data-brand-style="mono"]';

// Slate / navy → neutral gray at a similar lightness.
const RGB_MAP = [
  ['2, 6, 23', '6, 6, 6'], ['10, 14, 39', '10, 10, 10'], ['13, 17, 48', '16, 16, 16'], ['15, 23, 42', '18, 18, 18'],
  ['17, 24, 39', '20, 20, 20'], ['26, 31, 58', '30, 30, 30'], ['30, 41, 59', '38, 38, 38'], ['51, 65, 85', '64, 64, 64'],
  ['71, 85, 105', '82, 82, 82'], ['100, 116, 139', '115, 115, 115'], ['148, 163, 184', '163, 163, 163'],
  ['203, 213, 225', '212, 212, 212'], ['226, 232, 240', '229, 229, 229'], ['241, 245, 249', '244, 244, 244'], ['248, 250, 252', '250, 250, 250'],
];
const HEX_MAP = [
  ['#020617', '#060606'], ['#0f172a', '#121212'], ['#111827', '#141414'], ['#1e293b', '#262626'], ['#334155', '#404040'],
  ['#475569', '#525252'], ['#64748b', '#737373'], ['#94a3b8', '#a3a3a3'], ['#cbd5e1', '#d4d4d4'], ['#e2e8f0', '#e5e5e5'],
  ['#f1f5f9', '#f4f4f4'], ['#f8fafc', '#fafafa'], ['#0d1130', '#101010'], ['#0b0f2a', '#0e0e0e'], ['#0a0e27', '#0a0a0a'],
];

const RGB_RES = RGB_MAP.map(([from, to]) => [new RegExp(`(rgba?\\(\\s*)${from.replace(/, /g, '\\s*,\\s*')}`, 'gi'), `$1${to}`]);
const HEX_RES = HEX_MAP.map(([from, to]) => [new RegExp(`${from}(?![0-9a-f])`, 'gi'), to]);
const ANY = new RegExp([...RGB_MAP.map(([f]) => f.replace(/, /g, '\\s*,\\s*')), ...HEX_MAP.map(([f]) => f)].join('|'), 'i');

function neutralize(value) {
  let v = value;
  RGB_RES.forEach(([re, to]) => { v = v.replace(re, to); });
  HEX_RES.forEach(([re, to]) => { v = v.replace(re, to); });
  return v;
}

/** Split a selector list on top-level commas (not inside :is(), :not() …). */
function splitSelectors(text) {
  const out = [];
  let depth = 0;
  let cur = '';
  for (const ch of text) {
    if (ch === '(') depth += 1;
    if (ch === ')') depth -= 1;
    if (ch === ',' && depth === 0) { out.push(cur.trim()); cur = ''; } else cur += ch;
  }
  if (cur.trim()) out.push(cur.trim());
  return out;
}

/** Scope one selector to the mono style, in dark mode only or (for light-mode rules) as written. */
function scope(sel) {
  if (sel.includes('light-mode')) {
    if (/^html\b/.test(sel)) return sel.replace(/^html/, SCOPE);
    return `${SCOPE} ${sel}`;
  }
  if (/^:root\b/.test(sel)) return null;
  if (/^html\b/.test(sel)) return sel.replace(/^html/, SCOPE);
  if (/^body\b/.test(sel)) return sel.replace(/^body/, `${SCOPE} body:not(.light-mode)`);
  return `${SCOPE} body:not(.light-mode) ${sel}`;
}

function rulesFrom(list, out) {
  for (const rule of Array.from(list || [])) {
    if (rule.type === 1) { // CSSStyleRule
      const decls = [];
      const st = rule.style;
      for (let i = 0; i < st.length; i += 1) {
        const prop = st[i];
        if (prop.startsWith('--')) continue;
        const value = st.getPropertyValue(prop);
        if (value && ANY.test(value)) {
          const next = neutralize(value);
          if (next !== value) decls.push(`${prop}: ${next}${st.getPropertyPriority(prop) ? ' !important' : ''};`);
        }
      }
      if (decls.length) {
        const sels = splitSelectors(rule.selectorText || '').map(scope).filter(Boolean);
        if (sels.length) out.push(`${sels.join(', ')} { ${decls.join(' ')} }`);
      }
    } else if (rule.type === 4 && rule.cssRules) { // @media
      const inner = [];
      rulesFrom(rule.cssRules, inner);
      if (inner.length) out.push(`@media ${rule.conditionText || rule.media?.mediaText} { ${inner.join('\n')} }`);
    } else if (rule.cssRules) { // @supports and friends
      rulesFrom(rule.cssRules, out);
    }
  }
}

function build() {
  const out = [];
  for (const sheet of Array.from(document.styleSheets)) {
    if (sheet.ownerNode?.id === STYLE_ID) continue;
    let rules;
    try { rules = sheet.cssRules; } catch (_e) { continue; } // cross-origin (fonts, widgets)
    rulesFrom(rules, out);
  }
  return out.join('\n');
}

let observer = null;
let timer = null;

function render() {
  let el = document.getElementById(STYLE_ID);
  if (!el) {
    el = document.createElement('style');
    el.id = STYLE_ID;
    document.head.appendChild(el);
  }
  el.textContent = build();
  // Keep the override sheet last so it wins ties.
  if (el !== document.head.lastElementChild) document.head.appendChild(el);
}

/** Turn the neutral surfaces on or off. */
export function setMonoSurfaces(on) {
  if (typeof document === 'undefined') return;
  if (!on) {
    observer?.disconnect();
    observer = null;
    clearTimeout(timer);
    document.getElementById(STYLE_ID)?.remove();
    return;
  }
  render();
  if (!observer && typeof MutationObserver !== 'undefined') {
    observer = new MutationObserver((muts) => {
      // New stylesheets (lazy-loaded pages): rebuild once things settle.
      if (muts.some((m) => [...m.addedNodes].some((n) => (n.tagName === 'STYLE' || n.tagName === 'LINK') && n.id !== STYLE_ID))) {
        clearTimeout(timer);
        timer = setTimeout(render, 150);
      }
    });
    observer.observe(document.head, { childList: true });
  }
}

export const _internal = { neutralize, scope, splitSelectors };
